import { useRef } from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useTopic } from "@/hooks/useTopic";
import { isNullish } from "@theorgchartdev/storybook";
import { useReportingContext } from "@/providers/ReportsProvider";
import { isInReporting } from "@coreapp/utils/isInReporting";
import { isInBoxLayoutEditor } from "@coreapp/utils/isInBoxLayoutEditor";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { enumDataSource, enumUISubModes, enumFeaturesList } from "@coreapp/constants";
import { getFeatureAccess } from "@/Utils/Misc";

export const useAutoSaveHandler = ({ doc, iWiz }) => {
  const nav = useAppNavigate();
  const openCtrlRef = useRef(null);
  const docMgr = doc?.GetDocMgr();

  const { SaveReportChanges } = useReportingContext();

  useTopic("AutoSave", ({ open }) => {
    openCtrlRef.current?.setOpen(open);
  });

  const isUntitledDoc = () => {
    const curName = docMgr.GetFileName();
    return isNullish(curName) || curName?.length < 1 || curName === "";
  };

  const handleSaveClick = async () => {
    const wsMgr = GetWSMgr();

    if (isInBoxLayoutEditor()) {
      // DoExitBoxLayoutEditor → DoCommitAndExit is async; awaiting prevents BLE teardown
      // and toast from racing the in-flight network save.
      await wsMgr.DoExitBoxLayoutEditor(null, true, true);
      return;
    }

    if (isInReporting()) {
      SaveReportChanges();
      return;
    }

    if (isUntitledDoc()) {
      wsMgr.DoSaveDocument();
      return;
    }
    wsMgr.AutoSave(true);
  };

  const handleToggleAutoSave = () => {
    const wsMgr = GetWSMgr();
    const curName = docMgr.GetFileName(true);
    if (IsBackgroundFile(curName)) {
      wsMgr.SwitchSubMode(enumUISubModes.backgroundeditor);
    } else {
      nav.setMode("edit");
    }
    iWiz.fIsAutoSave = !iWiz.fIsAutoSave;
    doc.SetDirty(true);
  };

  const toggleAutoSaveChart = () => {
    const wsMgr = GetWSMgr();
    if (isUntitledDoc()) {
      wsMgr.DoSaveDocument(null, null, handleToggleAutoSave);
      return;
    }
    handleToggleAutoSave();
    if (!openCtrlRef?.current?.open) {
      openCtrlRef?.current?.setOpen(true);
    }
  };

  const chartDataSource = iWiz?.GetDataSource();
  const disableToggle =
    chartDataSource !== enumDataSource.chartbuilder || !getFeatureAccess(enumFeaturesList.autoSave);
  const currentFilename = docMgr?.GetFileName(true);
  const isBackground = currentFilename ? IsBackgroundFile(currentFilename) : false;
  const canAutoSave =
    chartDataSource === enumDataSource.chartbuilder || docMgr?.IsPlanChart() || isBackground;

  let autoSaveEnabled = iWiz?.fIsAutoSave || docMgr?.IsPlanChart();

  return {
    canAutoSave,
    autoSaveEnabled,
    disableToggle,
    toggleAutoSaveChart,
    handleSaveClick,
    isUntitledDoc,
    openCtrlRef
  };
};
